
import { useQuery } from "@tanstack/react-query";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import * as LucideIcons from "lucide-react";
import { ExternalLink } from "lucide-react";
import { FC } from "react";
import { LucideProps } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getPhotoUrl } from "@/services/photoService";
import type { PageSection, PageFeature } from "@/types/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";
import LanguageSelector from "@/components/LanguageSelector";
import { Skeleton } from "@/components/ui/skeleton";

// Filter out non-component exports from LucideIcons
const getIconComponents = () => {
  return Object.entries(LucideIcons).reduce((acc, [name, component]) => {
    if (typeof component === 'function' && name !== 'createLucideIcon') {
      acc[name] = component as FC<LucideProps>;
    }
    return acc;
  }, {} as Record<string, FC<LucideProps>>);
};

const SurroundingsPage = () => {
  const { language, t } = useLanguage();

  const { data: sections, isLoading: sectionsLoading } = useQuery({
    queryKey: ['page-sections', 'environs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('page_sections')
        .select('*')
        .eq('page', 'environs')
        .order('sort_order');
      
      if (error) throw error;
      return data as PageSection[];
    }
  });

  const { data: features, isLoading: featuresLoading } = useQuery({
    queryKey: ['page-features', 'environs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('page_features')
        .select('*')
        .eq('page', 'environs')
        .order('sort_order');
      
      if (error) throw error;
      return data as PageFeature[];
    }
  });

  // Get icon components
  const iconComponents = getIconComponents();

  // Translate section content
  const sectionsToTranslate = sections?.reduce((acc, section) => {
    acc[`title_${section.id}`] = section.title;
    acc[`content_${section.id}`] = section.content;
    return acc;
  }, {} as Record<string, string>) || {};

  const { translatedContent: translatedSections, isLoading: translationLoading } = 
    useTranslatedContent(sectionsToTranslate);

  // Translate feature content
  const featuresToTranslate = features?.reduce((acc, feature) => {
    acc[`title_${feature.id}`] = feature.title;
    acc[`description_${feature.id}`] = feature.description || "";
    return acc;
  }, {} as Record<string, string>) || {};

  const { translatedContent: translatedFeatures, isLoading: featuresTranslationLoading } = 
    useTranslatedContent(featuresToTranslate);

  // Static text to translate
  const staticText = {
    learnMore: "En savoir plus"
  };

  const { translatedContent: translatedStatic } = useTranslatedContent(staticText);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar rightElement={<LanguageSelector />} />
      
      <main className="flex-1 container mx-auto py-8 px-4 mt-16">
        {sectionsLoading ? (
          <div className="mb-8">
            <Skeleton className="h-10 w-1/2 mb-4" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          sections?.map((section) => (
            <div key={section.id} className="mb-8">
              <h1 className="text-3xl font-playfair text-villa-blue mb-4">
                {translationLoading 
                  ? <Skeleton className="h-8 w-1/3" /> 
                  : translatedSections[`title_${section.id}`] || section.title}
              </h1>
              <p className="text-gray-600 mb-8 max-w-3xl">
                {translationLoading 
                  ? <Skeleton className="h-16 w-full" /> 
                  : translatedSections[`content_${section.id}`] || section.content}
              </p>
            </div>
          ))
        )}

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featuresLoading ? (
            [...Array(6)].map((_, i) => (
              <Card key={`loading-${i}`}>
                <CardHeader>
                  <Skeleton className="h-8 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-10 w-32" />
                </CardContent>
              </Card>
            ))
          ) : (
            features?.map((feature) => {
              const Icon = feature.icon ? iconComponents[feature.icon] : null;
              return (
                <Card key={feature.id} className={feature.photo_path ? "overflow-hidden" : ""}>
                  {feature.photo_path && (
                    <div className="h-48 w-full overflow-hidden">
                      <img 
                        src={getPhotoUrl(feature.photo_path)} 
                        alt={translatedFeatures[`title_${feature.id}`] || feature.title}
                        className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className="h-8 w-8 text-villa-turquoise" />}
                      <CardTitle>
                        {featuresTranslationLoading 
                          ? <Skeleton className="h-6 w-32" /> 
                          : translatedFeatures[`title_${feature.id}`] || feature.title}
                      </CardTitle>
                    </div>
                    <CardDescription>
                      {featuresTranslationLoading 
                        ? <Skeleton className="h-12 w-full" /> 
                        : translatedFeatures[`description_${feature.id}`] || feature.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {feature.url && (
                      <Button variant="outline" asChild>
                        <a 
                          href={feature.url}
                          className="flex items-center"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {translatedStatic.learnMore}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    
                    {!feature.url && (
                      <Button variant="outline" disabled>
                        <span className="flex items-center">
                          {translatedStatic.learnMore}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </span>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SurroundingsPage;
